import React, { useCallback } from 'react';

import { dispatchMessage, MessagingEvents } from '@services/messagingService';

import { createEcommerceProduct, createModifyCartEvent, useTrackModifyCartEvent } from '../../../ecommerce';
import { IProduct } from '../../../model';
import {
  useDeleteAutoInsertedGiftAwaitable,
  useDeleteEngravingAwaitable,
  useDeleteTryItFirstAwaitable,
} from '../../../services';
import { IRemoveNonStandardProductAction, CartActions, ECartActions } from '../model';

export const useRemoveNonStandardProduct = (dispatch: React.Dispatch<CartActions>) => {
  const trackModifyCartEvent = useTrackModifyCartEvent();
  const { request: deleteAutoInsertedGiftRequest } = useDeleteAutoInsertedGiftAwaitable();
  const { request: deleteTryItFirstRequest } = useDeleteTryItFirstAwaitable();
  const { request: deleteEngravingRequest } = useDeleteEngravingAwaitable();

  return useCallback(
    async ({ product }: IRemoveNonStandardProductAction) => {
      const deleteProduct = async (data: IProduct) => {
        const { tryItFirstId, freeProduct, itemId, engraving, addedByCoupon } = data;

        if (tryItFirstId > 0) {
          return (await deleteTryItFirstRequest({ itemId }))?.data;
        } else if (freeProduct || addedByCoupon) {
          const deletedProductData = (await deleteAutoInsertedGiftRequest({ itemId }))?.data;
          if (deletedProductData) {
            dispatch({
              actionType: ECartActions.UPDATE_NON_STANDARD_PRODUCTS,
              payload: deletedProductData,
            });
          }
          return deletedProductData;
        } else if (engraving) {
          const result = (await deleteEngravingRequest({ itemId: engraving.cartItemId }))?.data;
          dispatchMessage({ event: MessagingEvents.ECartManipulationEvents.CART_UPDATE_EVENT });
          return result;
        }
        return null;
      };

      const deleteProductsData = await deleteProduct(product);

      if (deleteProductsData) {
        if (product.isLoyalty) {
          dispatch({ actionType: ECartActions.SET_REMOVED_LOYALTY_REWARD, payload: { loyaltyRewardRemoved: true } });
        }
        dispatch({
          actionType: ECartActions.UPDATE_NON_STANDARD_PRODUCTS,
          payload: deleteProductsData,
        });

        trackModifyCartEvent({
          ...createModifyCartEvent(product.count, 0),
          product: createEcommerceProduct(product),
        });
      }
    },
    [trackModifyCartEvent, deleteAutoInsertedGiftRequest, deleteTryItFirstRequest, deleteEngravingRequest, dispatch],
  );
};
